import React, { useState } from 'react';
import { SplitAndClassifyResponse } from '../types';

interface SplitAndClassifyResultsProps {
  results: SplitAndClassifyResponse;
  onDownload: (url: string) => void;
}

export const SplitAndClassifyResults: React.FC<SplitAndClassifyResultsProps> = ({ results, onDownload }) => {
  const { result, download_url, processing_times } = results;

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    classification: true,
    summary: false,
    processingTimes: false,
    detailedResponses: false
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate total tokens from all responses
  const totalTokens = [...result.run_responses, ...result.missing_pages_responses, ...result.boundary_responses]
    .reduce((acc, response) => ({
      input: acc.input + response.token_count.input_token,
      output: acc.output + response.token_count.output_token
    }), { input: 0, output: 0 });

  return (
    <div className="results">
      <div className="results-header">
        <h3>Processing Results</h3>
        <button
          className="btn btn-success"
          onClick={() => onDownload(download_url)}
        >
          Download Split Files
        </button>
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('classification')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.classification ? 'collapsed' : ''}`}
            aria-label={expandedSections.classification ? 'Collapse classification section' : 'Expand classification section'}
          >
            <span>▼</span>
          </button>
          <h4>Document Classification Results</h4>
        </div>
        {expandedSections.classification && (
          <div className="section-content">
            <div className="results-grid">
              {result.results.map((doc, index) => (
                <div key={index} className="result-card">
                  <div className="result-type">{doc.document_type}</div>
                  <div className="result-pages">
                    Pages: {doc.pages.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('summary')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.summary ? 'collapsed' : ''}`}
            aria-label={expandedSections.summary ? 'Collapse summary section' : 'Expand summary section'}
          >
            <span>▼</span>
          </button>
          <h4>Extraction Summary</h4>
        </div>
        {expandedSections.summary && (
          <div className="section-content">
            <div className="token-usage">
              <div className="token-item">
                <span className="token-label">Total Input Tokens:</span>
                <span className="token-value">{totalTokens.input.toLocaleString()}</span>
              </div>
              <div className="token-item">
                <span className="token-label">Total Output Tokens:</span>
                <span className="token-value">{totalTokens.output.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('processingTimes')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.processingTimes ? 'collapsed' : ''}`}
            aria-label={expandedSections.processingTimes ? 'Collapse processing times section' : 'Expand processing times section'}
          >
            <span>▼</span>
          </button>
          <h4>Processing Times</h4>
        </div>
        {expandedSections.processingTimes && (
          <div className="section-content">
            <ul className="processing-times">
              {Object.entries(processing_times).map(([key, time]) => (
                <li key={key} className="time-item">
                  <span className="time-label">{key}:</span>
                  <span className="time-value">{time.toFixed(2)}s</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('detailedResponses')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.detailedResponses ? 'collapsed' : ''}`}
            aria-label={expandedSections.detailedResponses ? 'Collapse detailed responses section' : 'Expand detailed responses section'}
          >
            <span>▼</span>
          </button>
          <h4>Detailed Responses</h4>
        </div>
        {expandedSections.detailedResponses && (
          <div className="section-content">
            <div className="response-group">
              <h5>Main Processing ({result.run_responses.length} runs)</h5>
              {result.run_responses.map((response, index) => (
                <div key={index} className="response-card">
                  <div className="response-header">
                    <span>Run {index + 1}</span>
                    <span>Time: {response.processing_time.toFixed(2)}s</span>
                    <span>Input: {response.token_count.input_token}, Output: {response.token_count.output_token}</span>
                    {response.model_info && (
                      <span>Model: {response.model_info.model_name} ({response.model_info.provider})</span>
                    )}
                  </div>
                  <div className="response-results">
                    {response.results.map((result, resultIndex) => (
                      <div key={resultIndex} className="mini-result">
                        {result.document_type} (Pages: {result.pages.join(', ')})
                      </div>
                    ))}
                  </div>
                  {response.prompt && (
                    <details className="response-details">
                      <summary>Show Prompt</summary>
                      <pre className="prompt-text">{response.prompt}</pre>
                    </details>
                  )}
                  {response.raw_value && (
                    <details className="response-details">
                      <summary>Show Raw Response</summary>
                      <pre className="raw-text">{response.raw_value}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>

            {result.missing_pages_responses.length > 0 && (
              <div className="response-group">
                <h5>Missing Pages Processing ({result.missing_pages_responses.length} runs)</h5>
                {result.missing_pages_responses.map((response, index) => (
                  <div key={index} className="response-card">
                    <div className="response-header">
                      <span>Missing Pages Run {index + 1}</span>
                      <span>Time: {response.processing_time.toFixed(2)}s</span>
                      <span>Input: {response.token_count.input_token}, Output: {response.token_count.output_token}</span>
                      {response.model_info && (
                        <span>Model: {response.model_info.model_name} ({response.model_info.provider})</span>
                      )}
                    </div>
                    <div className="response-results">
                      {response.results.map((result, resultIndex) => (
                        <div key={resultIndex} className="mini-result">
                          {result.document_type} (Pages: {result.pages.join(', ')})
                        </div>
                      ))}
                    </div>
                    {response.prompt && (
                      <details className="response-details">
                        <summary>Show Prompt</summary>
                        <pre className="prompt-text">{response.prompt}</pre>
                      </details>
                    )}
                    {response.raw_value && (
                      <details className="response-details">
                        <summary>Show Raw Response</summary>
                        <pre className="raw-text">{response.raw_value}</pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}

            {result.boundary_responses.length > 0 && (
              <div className="response-group">
                <h5>Boundary Processing ({result.boundary_responses.length} runs)</h5>
                {result.boundary_responses.map((response, index) => (
                  <div key={index} className="response-card">
                    <div className="response-header">
                      <span>Boundary Run {index + 1}</span>
                      <span>Time: {response.processing_time.toFixed(2)}s</span>
                      <span>Input: {response.token_count.input_token}, Output: {response.token_count.output_token}</span>
                      {response.model_info && (
                        <span>Model: {response.model_info.model_name} ({response.model_info.provider})</span>
                      )}
                    </div>
                    <div className="response-results">
                      {response.results.map((result, resultIndex) => (
                        <div key={resultIndex} className="mini-result">
                          {result.document_type} (Pages: {result.pages.join(', ')})
                        </div>
                      ))}
                    </div>
                    {response.prompt && (
                      <details className="response-details">
                        <summary>Show Prompt</summary>
                        <pre className="prompt-text">{response.prompt}</pre>
                      </details>
                    )}
                    {response.raw_value && (
                      <details className="response-details">
                        <summary>Show Raw Response</summary>
                        <pre className="raw-text">{response.raw_value}</pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};